/**
 * Constantes de nómina — Valores legales Colombia y catálogos de selección
 *
 * Fuente única de verdad para:
 *   - Valores legales por defecto (SMMLV, auxilio, porcentajes)
 *   - Catálogos de selección (tipos de contrato, modalidades de pago, motivos de retiro)
 *   - Labels y traducciones de enums
 *
 * NO debe contener lógica de cálculo. Solo datos estáticos.
 */
import type {
  TipoContrato,
  ModalidadPago,
  TipoTrabajador,
  MotivoRetiro,
} from "../types/payroll.types";
import { COLOMBIA_PAYROLL_PLUGIN } from "@/config/payroll";
import type { PayrollLegalProfile } from "@/config/payroll";

// ─── Valores Legales por Defecto (Colombia 2026) ────────────────────────────

const colombiaLegalProfile = COLOMBIA_PAYROLL_PLUGIN.legalProfile;
if (!colombiaLegalProfile) {
  throw new Error("El plugin legal de Colombia requiere un perfil de nómina.");
}

export const DEFAULTS_2026: PayrollLegalProfile = colombiaLegalProfile;

/**
 * Perfil legal para cálculos (Colombia por defecto).
 * Futuro: instanciar según `countryCode` + año desde configuración persistida.
 */
export type LegalPayrollProfile = PayrollLegalProfile;

export const getDefaultLegalProfile = (): LegalPayrollProfile => DEFAULTS_2026;

/** Días base de nómina mensual */
export const DIAS_MES_NOMINA = 30;

/** Días base anual para prestaciones */
export const DIAS_ANIO_NOMINA = 360;

/** Jornada laboral ordinaria en horas */
export const HORAS_JORNADA = 8;

/** Jornada máxima legal semanal desde julio de 2026 */
export const HORAS_SEMANALES_LEGALES_2026 = 42;

/** Base mensual de nómina para valor hora (desde 2026: 228h en esta app) */
export const HORAS_MES_NOMINA = 228;

// ─── Catálogos de Selección ─────────────────────────────────────────────────

export const TIPOS_CONTRATO: { value: TipoContrato; label: string }[] = [
  { value: "indefinido", label: "Término Indefinido" },
  { value: "fijo", label: "Término Fijo" },
  { value: "obra_labor", label: "Obra o Labor" },
  { value: "prestacion", label: "Prestación de Servicios" },
];

export const MODALIDADES_PAGO: {
  value: ModalidadPago;
  label: string;
  descripcion: string;
}[] = [
  {
    value: "mensual",
    label: "Mensual",
    descripcion: "Salario fijo mensual con deducciones de ley",
  },
  {
    value: "quincenal",
    label: "Quincenal",
    descripcion: "Pago cada 15 días, deducciones proporcionales",
  },
  {
    value: "diario",
    label: "Por Día",
    descripcion: "Pago por jornada trabajada",
  },
  {
    value: "por_horas",
    label: "Por Horas",
    descripcion: "Pago por horas trabajadas",
  },
];

export const TIPOS_TRABAJADOR: {
  value: TipoTrabajador;
  label: string;
  descripcion: string;
}[] = [
  {
    value: "formal",
    label: "Formal",
    descripcion: "Contrato laboral con prestaciones sociales",
  },
  {
    value: "informal",
    label: "Informal",
    descripcion: "Sin contrato formal, pago directo sin prestaciones",
  },
];

export const MOTIVOS_RETIRO: { value: MotivoRetiro; label: string }[] = [
  { value: "sin_justa_causa", label: "Despido sin Justa Causa" },
  { value: "con_justa_causa", label: "Despido con Justa Causa" },
  { value: "renuncia_voluntaria", label: "Renuncia Voluntaria" },
  { value: "fin_contrato", label: "Terminación de Contrato / Obra" },
];

// ─── Labels / Traducciones ──────────────────────────────────────────────────

export const CONTRATO_LABELS: Record<TipoContrato, string> = {
  indefinido: "Indefinido",
  fijo: "Término Fijo",
  obra_labor: "Obra o Labor",
  prestacion: "Prestación",
};

export const MODALIDAD_LABELS: Record<ModalidadPago, string> = {
  mensual: "Mensual",
  quincenal: "Quincenal",
  diario: "Por Día",
  por_horas: "Por Horas",
};

export const TRABAJADOR_LABELS: Record<TipoTrabajador, string> = {
  formal: "Formal",
  informal: "Informal",
};

/** Filtros de tipo contrato para SearchBar/FilterChips */
export const FILTROS_CONTRATO = [
  { value: "", label: "Todos" },
  { value: "indefinido", label: "Indefinido" },
  { value: "fijo", label: "Fijo" },
  { value: "obra_labor", label: "Obra Labor" },
  { value: "prestacion", label: "Prestación" },
];

/** Filtros de modalidad de pago */
export const FILTROS_MODALIDAD = [
  { value: "", label: "Todas" },
  { value: "mensual", label: "Mensual" },
  { value: "quincenal", label: "Quincenal" },
  { value: "diario", label: "Por Día" },
  { value: "por_horas", label: "Por Horas" },
];
